import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import { Popover } from './popover'
import { cn } from './cn'

export interface InfoTooltipProps {
  /** Main line of the tooltip — one short sentence. */
  title: ReactNode
  /** Optional extra paragraph(s) below the title. */
  children?: ReactNode
  /** Accessible aria-label for the icon button. Default "Más información". */
  ariaLabel?: string
  /** Placement of the panel. Default `bottom-start`. */
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  /** Icon size. Default 12 — sits cleanly inline next to a caps label. */
  iconSize?: number
  className?: string
}

/**
 * Inline help affordance — a small info icon that opens a short popover
 * explaining what a field / metric / toggle does. Canonical component
 * for "spot explanations" referenced in `docs/ui-ux-patterns.md §13`.
 *
 * Pair with column headers, KPI labels, or toggle labels where the
 * semantic isn't obvious from the word alone (e.g. "Fiabilidad",
 * "Tasa no-show", policy toggles, priority fields).
 */
export function InfoTooltip({
  title,
  children,
  ariaLabel = 'Más información',
  placement = 'bottom-start',
  iconSize = 12,
  className,
}: InfoTooltipProps) {
  return (
    <Popover
      placement={placement}
      trigger={
        <button
          type="button"
          className={cn('info-tooltip-trigger', className)}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        >
          <Info size={iconSize} />
        </button>
      }
    >
      <div className="info-tooltip-panel" role="note">
        <div className="info-tooltip-title">{title}</div>
        {children && <div className="info-tooltip-body">{children}</div>}
      </div>
    </Popover>
  )
}
